import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowClockwise, Users, CurrencyDollar, CalendarCheck, TrendUp, Crown, DeviceMobile, DownloadSimple, Flag, CheckCircle, Warning } from "@phosphor-icons/react";
import { api, API } from "../../lib/api";

const money = (v) => "$" + Math.round(v || 0).toLocaleString();

function RffCard() {
  const [rff, setRff] = useState(null);

  const load = () => api.get("/rff/status").then((r) => setRff(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const toggle = async () => {
    try {
      if (rff.published) {
        await api.delete("/rff/publish");
      } else {
        await api.post("/rff/publish");
      }
      load();
    } catch {
      // retry via refresh
    }
  };

  if (!rff) return null;
  const friday = new Date(rff.week + "T12:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric" });

  return (
    <div
      className={`mt-6 rounded-sm border p-7 md:p-8 flex flex-col md:flex-row md:items-center gap-5 ${
        rff.published ? "bg-card border-border" : "bg-accent/5 border-accent/40"
      }`}
      data-testid="admin-rff-card"
    >
      <Flag size={26} weight="fill" className="text-accent shrink-0" />
      <div className="flex-1">
        <p className="font-serif text-xl text-primary tracking-tight">Red Flag Friday — episode for {friday}</p>
        <p className="mt-1 text-sm text-foreground/60">
          {rff.published
            ? "Marked live. This week's episode is taken care of."
            : "Not marked yet — write or schedule this week's episode, then tick it off here."}
        </p>
        {rff.recent.length > 0 && (
          <p className="mt-2 text-xs text-foreground/45">
            Recently published: {rff.recent.map((r) => new Date(r.week + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })).join(" · ")}
          </p>
        )}
      </div>
      <div className="flex items-center gap-4 shrink-0">
        <span
          className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider border rounded-full px-4 py-2 ${
            rff.published ? "text-primary border-primary/30 bg-primary/10" : "text-accent border-accent/40 bg-accent/10"
          }`}
          data-testid="admin-rff-status"
        >
          {rff.published ? <CheckCircle size={14} weight="fill" /> : <Warning size={14} weight="fill" />}
          {rff.published ? "Published" : "Due"}
        </span>
        <button
          data-testid="admin-rff-toggle"
          onClick={toggle}
          className="text-xs font-bold uppercase tracking-wider bg-primary text-primary-foreground rounded-full px-5 py-2.5 hover:bg-accent transition-colors duration-300"
        >
          {rff.published ? "Unmark" : "Mark published"}
        </button>
      </div>
    </div>
  );
}

export default function Overview() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    api.get("/analytics/summary").then((r) => setData(r.data)).catch(() => setError("Could not load analytics. Please try again."));
  };

  useEffect(() => {
    load();
  }, []);

  const cards = data
    ? [
        { label: "Enquiries this month", value: data.leads_this_month, icon: CalendarCheck, testId: "admin-stat-leads-month" },
        { label: "Total enquiries", value: data.total_leads, icon: Users, testId: "admin-stat-leads-total" },
        { label: "Valuations this month", value: money(data.valuations_this_month), icon: CurrencyDollar, testId: "admin-stat-valuations-month" },
        { label: "Valuations all time", value: money(data.valuations_total), icon: TrendUp, testId: "admin-stat-valuations-total" },
      ]
    : [];

  const maxCount = data?.top_brands?.length ? Math.max(...data.top_brands.map((b) => b.count)) : 1;

  return (
    <div data-testid="admin-overview">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-primary tracking-tight">Analytics summary</h1>
          <p className="mt-2 text-sm text-foreground/60">Enquiries, valuations and the brands owners ask about most.</p>
        </div>
        <button
          data-testid="admin-overview-refresh"
          onClick={load}
          className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors duration-300"
        >
          <ArrowClockwise size={15} weight="bold" /> Refresh
        </button>
      </div>
      {error && <p className="mt-4 text-sm font-semibold text-destructive" data-testid="admin-overview-error">{error}</p>}

      <RffCard />

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {(data ? cards : Array.from({ length: 4 })).map((c, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="bg-card border border-border rounded-sm p-7"
            data-testid={c?.testId || `admin-stat-loading-${i}`}
          >
            {c ? (
              <>
                <c.icon size={24} weight="duotone" className="text-accent" />
                <p className="mt-5 font-serif text-4xl text-primary tracking-tight">{c.value}</p>
                <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{c.label}</p>
              </>
            ) : (
              <div className="h-24 animate-pulse bg-muted rounded-sm" />
            )}
          </motion.div>
        ))}
      </div>

      <div className="mt-10 grid lg:grid-cols-3 gap-6 items-start">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="lg:col-span-2 bg-card border border-border rounded-sm p-8 md:p-10"
        data-testid="admin-top-brands"
      >
        <div className="flex items-center gap-3">
          <Crown size={22} weight="duotone" className="text-accent" />
          <h2 className="font-serif text-2xl text-primary tracking-tight">Most searched vacation brands</h2>
        </div>
        <p className="mt-2 text-xs text-foreground/50">Combined from the brand search and consultation form submissions.</p>
        <div className="mt-8 space-y-4">
          {data && data.top_brands.length === 0 && (
            <p className="text-sm text-foreground/50 py-6" data-testid="admin-brands-empty">
              No brand searches yet — they'll rank here as visitors use the brand search and consultation form.
            </p>
          )}
          {data &&
            data.top_brands.map((b, i) => (
              <div key={b.brand + i} className="flex items-center gap-4" data-testid={`admin-brand-row-${i}`}>
                <span className="font-serif text-lg text-muted-foreground w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="w-40 md:w-56 shrink-0 text-sm font-semibold text-foreground truncate">{b.brand}</span>
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(b.count / maxCount) * 100}%` }}
                    transition={{ duration: 0.8, delay: 0.4 + i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                    className="h-full bg-accent rounded-full"
                  />
                </div>
                <span className="w-12 text-right text-sm font-bold text-foreground/70">{b.count}</span>
              </div>
            ))}
          {!data && <div className="h-32 animate-pulse bg-muted rounded-sm" />}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="bg-primary text-primary-foreground rounded-sm p-8 md:p-10"
        data-testid="admin-waitlist-card"
      >
        <div className="flex items-center gap-3">
          <DeviceMobile size={22} weight="duotone" className="text-accent" />
          <h2 className="font-serif text-2xl tracking-tight">App launch list</h2>
        </div>
        <p className="mt-2 text-xs text-primary-foreground/50">Owners waiting for the companion app.</p>
        <p className="mt-8 font-serif text-6xl tracking-tight" data-testid="admin-waitlist-count">
          {data ? data.waitlist_count : "—"}
        </p>
        <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.2em] text-primary-foreground/45">
          {data && data.waitlist_count === 1 ? "signup" : "signups"} so far
        </p>
        <div className="mt-8 border-t border-primary-foreground/15 pt-6 space-y-3">
          {data && data.waitlist_recent.length === 0 && (
            <p className="text-xs text-primary-foreground/50" data-testid="admin-waitlist-empty">No signups yet — share the site and watch this grow.</p>
          )}
          {data &&
            data.waitlist_recent.map((w, i) => (
              <div key={i} className="flex items-center justify-between gap-4 text-xs" data-testid={`admin-waitlist-row-${i}`}>
                <span className="font-semibold text-primary-foreground/80 truncate">{w.email}</span>
                <span className="text-primary-foreground/40 shrink-0">
                  {new Date(w.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </span>
              </div>
            ))}
        </div>
        <a
          data-testid="admin-waitlist-export"
          href={`${API}/app-waitlist/export`}
          className="mt-8 flex items-center justify-center gap-2 border border-primary-foreground/25 text-primary-foreground/80 text-xs font-bold uppercase tracking-[0.15em] rounded-full px-5 py-3 hover:border-accent hover:text-accent transition-colors duration-300"
        >
          <DownloadSimple size={14} weight="bold" /> Download launch list
        </a>
        <div className="mt-5 flex items-center justify-between text-xs" data-testid="admin-templates-row">
          <span className="text-primary-foreground/50 font-semibold">
            Letter template downloads: <span className="text-primary-foreground" data-testid="admin-templates-count">{data ? data.template_downloads_count : "—"}</span>
          </span>
          <a
            data-testid="admin-templates-export"
            href={`${API}/template-downloads/export`}
            className="font-bold uppercase tracking-wider text-accent hover:text-primary-foreground transition-colors duration-300"
          >
            Export
          </a>
        </div>
      </motion.div>
      </div>
    </div>
  );
}
